import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  User,
  FileText,
  Wallet,
  Shield,
  Check,
  X,
  CreditCard,
  Trash2,
  Calendar,
  Globe,
  Phone,
  Building2,
  ChevronLeft,
  Rocket,
  Clock,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

// Background images for each tab
const tabBackgrounds = {
  profile: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1200&q=80',
  warranty: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&q=80',
  financial: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&q=80',
  documents: 'https://images.unsplash.com/photo-1568667256549-094345857637?w=1200&q=80'
};

// Tab configuration
const tabs = [
  { id: 'profile', label: 'Profile', labelAr: 'الملف', icon: User },
  { id: 'warranty', label: 'Warranty', labelAr: 'الضمان', icon: Shield },
  { id: 'financial', label: 'Financial', labelAr: 'المالية', icon: Wallet },
  { id: 'documents', label: 'Info', labelAr: 'معلومات', icon: FileText }
];

// Warranty status configuration
const warrantyStatusConfig = {
  'active': { bg: 'from-emerald-500 to-emerald-600', text: 'ACTIVE', textAr: 'نشط', icon: Check, color: 'emerald' },
  'warning': { bg: 'from-amber-500 to-amber-600', text: 'WARNING', textAr: 'تحذير', icon: AlertTriangle, color: 'amber' },
  'critical': { bg: 'from-emerald-500 to-green-600', text: 'COMPLETED', textAr: 'تم الضمان', icon: CheckCircle, color: 'emerald' },
  'expired': { bg: 'from-slate-500 to-slate-600', text: 'EXPIRED', textAr: 'منتهي', icon: X, color: 'slate' },
  'not_activated': { bg: 'from-blue-500 to-blue-600', text: 'NOT ACTIVATED', textAr: 'غير مفعل', icon: Clock, color: 'blue' }
};

// Animation variants
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

// Country flags
const COUNTRY_FLAGS = {
  'UAE': '🇦🇪', 'Saudi Arabia': '🇸🇦', 'Oman': '🇴🇲', 'Qatar': '🇶🇦',
  'Kuwait': '🇰🇼', 'Bahrain': '🇧🇭', 'Jordan': '🇯🇴', 'Egypt': '🇪🇬'
};

// Helper function to format base64 image with proper data URI prefix
const formatBase64Image = (base64String) => {
  if (!base64String) return null;
  if (base64String.startsWith('data:image')) return base64String;
  return `data:image/jpeg;base64,${base64String}`;
};

// Worker Header Component - Shows in ALL tabs
const WorkerHeader = ({ worker, currencySymbol }) => {
  const warrantyStatus = worker.warranty_activated 
    ? (worker.warranty_status || 'active') 
    : 'not_activated';
  const config = warrantyStatusConfig[warrantyStatus] || warrantyStatusConfig['active'];
  const StatusIcon = config.icon;
  const initials = (worker.name || 'NA').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  const countryFlag = COUNTRY_FLAGS[worker.country] || '🌍';
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-4 mb-4 border border-white/10"
    >
      <div className="flex items-center gap-4">
        {/* Worker Photo */}
        {worker.photo ? (
          <div className="w-16 h-16 rounded-2xl border-2 border-orange-500/50 overflow-hidden shadow-lg shadow-orange-500/20 flex-shrink-0">
            <img src={formatBase64Image(worker.photo)} alt={worker.name} className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="w-16 h-16 rounded-2xl border-2 border-orange-500/50 bg-gradient-to-br from-orange-500/20 to-orange-600/20 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xl font-bold">{initials}</span>
          </div>
        )}
        
        {/* Worker Info */}
        <div className="flex-1 min-w-0">
          <h2 className="text-white text-lg font-bold truncate">{(worker.name || 'N/A').toUpperCase()}</h2>
          <p className="text-orange-400 text-sm font-medium flex items-center gap-1">
            <span>{countryFlag}</span>
            {worker.country || 'Unknown'}
          </p>
        </div>
        
        {/* Warranty Status Badge */}
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r ${config.bg} flex-shrink-0`}>
          <StatusIcon className="h-4 w-4 text-white" />
          <span className="text-white text-xs font-bold">{config.text}</span>
        </div>
      </div>
    </motion.div>
  );
};

// Profile Tab Content
const ProfileTab = ({ worker }) => {
  const countryFlag = COUNTRY_FLAGS[worker.country] || '🌍';
  
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="space-y-4"
    >
      {/* Personal Details */}
      <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
        <h3 className="text-white text-base font-semibold mb-4 flex items-center gap-2">
          <User className="h-5 w-5 text-cyan-400" />
          Personal Details / البيانات الشخصية
        </h3>
        
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-800/60 rounded-xl p-3 border border-white/5">
            <p className="text-slate-400 text-xs mb-1">Name / الاسم</p>
            <p className="text-white font-medium text-sm">{worker.name || 'N/A'}</p>
          </div>
          
          <div className="bg-slate-800/60 rounded-xl p-3 border border-white/5">
            <p className="text-slate-400 text-xs mb-1">Country / الدولة</p>
            <p className="text-white font-medium text-sm flex items-center gap-2">
              <span>{countryFlag}</span>
              {worker.country || 'N/A'}
            </p>
          </div>
          
          <div className="bg-slate-800/60 rounded-xl p-3 border border-white/5">
            <p className="text-slate-400 text-xs mb-1">Phone / الهاتف</p>
            <p className="text-white font-medium text-sm flex items-center gap-2">
              <Phone className="h-4 w-4 text-emerald-400" />
              {worker.phone || 'N/A'}
            </p>
          </div>
          
          <div className="bg-slate-800/60 rounded-xl p-3 border border-white/5">
            <p className="text-slate-400 text-xs mb-1">Client / العميل</p>
            <p className="text-white font-medium text-sm flex items-center gap-2">
              <Building2 className="h-4 w-4 text-amber-400" />
              {worker.client_name || 'Not Assigned'}
            </p>
          </div>
        </div>
      </div>
      
      {/* ID Information */}
      {worker.id_number && (
        <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
          <h3 className="text-white text-base font-semibold mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-400" />
            ID Information / معلومات الهوية
          </h3>
          
          <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
            <p className="text-slate-400 text-xs mb-1">ID Number / رقم الهوية</p>
            <p className="text-blue-400 font-bold text-lg">{worker.id_number}</p>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Warranty Tab Content
const WarrantyTab = ({ worker, onActivateWarranty }) => {
  const warrantyStatus = worker.warranty_activated 
    ? (worker.warranty_status || 'active') 
    : 'not_activated';
  const config = warrantyStatusConfig[warrantyStatus] || warrantyStatusConfig['active'];
  
  // Calculate warranty progress
  const warrantyDaysTotal = (worker.warranty_period || 12) * 30;
  const warrantyDaysRemaining = worker.warranty_days_remaining || 0;
  const warrantyDaysUsed = warrantyDaysTotal - warrantyDaysRemaining;
  const progressPercent = Math.min(100, Math.max(0, (warrantyDaysUsed / warrantyDaysTotal) * 100));
  
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="space-y-4"
    >
      {/* Warranty Status */}
      <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
        <h3 className="text-white text-base font-semibold mb-4 flex items-center gap-2">
          <Shield className="h-5 w-5 text-emerald-400" />
          Warranty Status / حالة الضمان
        </h3>
        
        {worker.warranty_activated ? (
          <div className="space-y-4">
            {/* Status Badge */}
            <div className={`rounded-xl p-4 border bg-gradient-to-r ${config.bg}/20 border-${config.color}-500/30`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl bg-${config.color}-500/20 flex items-center justify-center`}>
                    <config.icon className={`h-6 w-6 text-${config.color}-400`} />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">{config.text}</p>
                    <p className="text-slate-400 text-sm">{config.textAr}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white font-bold text-2xl">{warrantyDaysRemaining}</p>
                  <p className="text-slate-400 text-xs">Days Left / يوم متبقي</p>
                </div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Progress / التقدم</span>
                <span className="text-white font-medium">{Math.round(progressPercent)}%</span>
              </div>
              <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercent}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className={`h-full rounded-full ${
                    warrantyStatus === 'critical' ? 'bg-gradient-to-r from-emerald-500 to-green-500' :
                    warrantyStatus === 'warning' ? 'bg-gradient-to-r from-amber-500 to-orange-500' :
                    'bg-gradient-to-r from-emerald-500 to-green-400'
                  }`}
                />
              </div>
            </div>
            
            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-800/60 rounded-xl p-3 border border-white/5">
                <p className="text-slate-400 text-xs mb-1">Start Date / تاريخ البدء</p>
                <p className="text-white font-medium text-sm flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-emerald-400" />
                  {worker.warranty_start_date || 'N/A'}
                </p>
              </div>
              
              <div className="bg-slate-800/60 rounded-xl p-3 border border-white/5">
                <p className="text-slate-400 text-xs mb-1">End Date / تاريخ الانتهاء</p>
                <p className="text-white font-medium text-sm flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-red-400" />
                  {worker.warranty_end_date || 'N/A'}
                </p>
              </div>
            </div>
            
            {/* Warranty Period */}
            <div className="bg-slate-800/60 rounded-xl p-4 border border-white/5 text-center">
              <p className="text-slate-400 text-xs mb-1">Warranty Period / فترة الضمان</p>
              <p className="text-white font-bold text-2xl">{worker.warranty_period || 12} <span className="text-sm font-normal text-slate-400">months / شهر</span></p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-blue-500/10 rounded-xl p-6 border border-blue-500/30 text-center">
              <Clock className="h-12 w-12 text-blue-400 mx-auto mb-3" />
              <p className="text-white font-bold text-lg mb-1">Warranty Not Activated</p>
              <p className="text-slate-400 text-sm mb-4">الضمان غير مفعل بعد</p>
              
              <Button
                onClick={() => onActivateWarranty(worker)}
                className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-bold rounded-xl py-4 px-6"
              >
                <Rocket className="h-5 w-5 mr-2" />
                Activate Warranty / تفعيل الضمان
              </Button>
            </div>
            
            {/* Warranty Period Info */}
            <div className="bg-slate-800/60 rounded-xl p-4 border border-white/5 text-center">
              <p className="text-slate-400 text-xs mb-1">Warranty Period / فترة الضمان</p>
              <p className="text-white font-bold text-2xl">{worker.warranty_period || 12} <span className="text-sm font-normal text-slate-400">months / شهر</span></p>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Financial Tab Content
const FinancialTab = ({ worker, currencySymbol, onPayment }) => {
  const remaining = (worker.cost || 0) - (worker.paid || 0);
  const paymentLog = worker.payment_history || [];
  
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="space-y-4"
    >
      {/* Financial Summary */}
      <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
        <h3 className="text-white text-base font-semibold mb-4 flex items-center gap-2">
          <Wallet className="h-5 w-5 text-emerald-400" />
          Financial Summary / الملخص المالي
        </h3>
        
        <div className="space-y-3">
          {/* Cost */}
          <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30">
            <div className="flex justify-between items-center">
              <span className="text-amber-300 text-sm">Total Cost / التكلفة الكلية</span>
              <span className="text-amber-400 text-xl font-bold">{currencySymbol} {(worker.cost || 0).toLocaleString()}</span>
            </div>
          </div>
          
          {/* Paid */}
          <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/30">
            <div className="flex justify-between items-center">
              <span className="text-emerald-300 text-sm">Paid / المدفوع</span>
              <span className="text-emerald-400 text-xl font-bold">{currencySymbol} {(worker.paid || 0).toLocaleString()}</span>
            </div>
          </div>
          
          {/* Remaining */}
          <div className={`rounded-xl p-4 border ${remaining > 0 ? 'bg-rose-500/10 border-rose-500/30' : 'bg-emerald-500/10 border-emerald-500/30'}`}>
            <div className="flex justify-between items-center">
              <span className={`text-sm ${remaining > 0 ? 'text-rose-300' : 'text-emerald-300'}`}>
                {remaining > 0 ? 'Remaining / المتبقي' : 'Fully Paid / تم الدفع'}
              </span>
              <span className={`text-xl font-bold ${remaining > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                {currencySymbol} {Math.abs(remaining).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Payment Action */}
      {remaining > 0 && (
        <Button
          onClick={() => onPayment(worker)}
          className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-bold rounded-xl py-5"
        >
          <CreditCard className="h-5 w-5 mr-2" />
          Add Payment / إضافة دفعة
        </Button>
      )}
      
      {/* Payment History */}
      {paymentLog.length > 0 && (
        <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
          <h3 className="text-white text-base font-semibold mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-cyan-400" />
            Payment History / سجل المدفوعات
          </h3>
          
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {paymentLog.map((payment, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-emerald-500/10 rounded-xl p-3 border border-emerald-500/20"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <CreditCard className="h-4 w-4 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-emerald-400 font-bold text-sm">+{currencySymbol} {(payment.amount || 0).toLocaleString()}</p>
                      {payment.notes && <p className="text-slate-500 text-xs">{payment.notes}</p>}
                    </div>
                  </div>
                  <p className="text-slate-500 text-xs">{payment.date}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Documents/Info Tab Content
const DocumentsTab = ({ worker }) => {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="space-y-4"
    >
      {/* Contract Info */}
      <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
        <h3 className="text-white text-base font-semibold mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-blue-400" />
          Contract Information / معلومات العقد
        </h3>
        
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-800/60 rounded-xl p-3 border border-white/5">
            <p className="text-slate-400 text-xs mb-1">Worker ID</p>
            <p className="text-white font-medium text-xs break-all">{worker.id || 'N/A'}</p>
          </div>
          
          <div className="bg-slate-800/60 rounded-xl p-3 border border-white/5">
            <p className="text-slate-400 text-xs mb-1">Client ID</p>
            <p className="text-white font-medium text-xs break-all">{worker.client_id || 'N/A'}</p>
          </div>
        </div>
      </div>
      
      {/* Notes */}
      {worker.notes && (
        <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
          <h3 className="text-white text-base font-semibold mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-400" />
            Notes / ملاحظات
          </h3>
          
          <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/20">
            <p className="text-white text-sm whitespace-pre-wrap">{worker.notes}</p>
          </div>
        </div>
      )}
      
      {/* Created Date */}
      <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
        <h3 className="text-white text-base font-semibold mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-purple-400" />
          Record Info / معلومات السجل
        </h3>
        
        <div className="bg-slate-800/60 rounded-xl p-3 border border-white/5">
          <p className="text-slate-400 text-xs mb-1">Created At / تاريخ الإنشاء</p>
          <p className="text-white font-medium text-sm">
            {worker.created_at ? new Date(worker.created_at).toLocaleDateString() : 'N/A'}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Main OverseasWorkerDetailsModal Component
const OverseasWorkerDetailsModal = ({ 
  open, 
  onClose, 
  worker, 
  currencySymbol = 'AED',
  onPayment,
  onActivateWarranty,
  onDelete
}) => {
  const [activeTab, setActiveTab] = useState('profile');
  
  // Reset to profile tab when modal opens
  useEffect(() => {
    if (open) {
      setActiveTab('profile');
    }
  }, [open]);
  
  if (!worker) return null;
  
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 bg-transparent border-0 overflow-hidden">
        <div className="relative w-full h-full rounded-2xl overflow-hidden">
          {/* Animated Background */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${tabBackgrounds[activeTab]})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/95 to-slate-950" />
            </motion.div>
          </AnimatePresence>
          
          {/* Content */}
          <div className="relative z-10 h-full flex flex-col max-h-[90vh]">
            {/* Fixed Header */}
            <div className="p-4 border-b border-white/10 bg-slate-950/80 backdrop-blur-xl flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="text-slate-400 hover:text-white"
                >
                  <ChevronLeft className="h-5 w-5 mr-1" />
                  Back
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(worker)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
              
              {/* Tab Navigation */}
              <div className="flex gap-1.5">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-xl transition-all duration-300 ${
                        isActive 
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25' 
                          : 'bg-slate-800/60 text-slate-400 hover:bg-slate-700 hover:text-white'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="text-xs font-medium">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Worker Header - Shows in ALL tabs */}
              <WorkerHeader worker={worker} currencySymbol={currencySymbol} />
              
              {/* Tab Content */}
              <AnimatePresence mode="wait">
                {activeTab === 'profile' && (
                  <ProfileTab key="profile" worker={worker} />
                )}
                {activeTab === 'warranty' && (
                  <WarrantyTab 
                    key="warranty" 
                    worker={worker} 
                    onActivateWarranty={onActivateWarranty}
                  />
                )}
                {activeTab === 'financial' && (
                  <FinancialTab 
                    key="financial"
                    worker={worker}
                    currencySymbol={currencySymbol}
                    onPayment={onPayment}
                  />
                )}
                {activeTab === 'documents' && (
                  <DocumentsTab key="documents" worker={worker} />
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OverseasWorkerDetailsModal;
